// src/services/surveyService.js
import axios from 'axios';

// Base API configuration
const getEnvVar = (viteName, fallback) => {
  try {
    if (import.meta && import.meta.env && import.meta.env[viteName]) {
      return import.meta.env[viteName];
    }
  } catch (e) {
    // import.meta not available
  }
  return fallback;
};

// CRITICAL FIX: Use port 8080 to match backend
const API_BASE_URL = getEnvVar('VITE_API_URL', 'http://localhost:8080');
const API_TIMEOUT = parseInt(getEnvVar('VITE_API_TIMEOUT', '30000'));
const MAX_RETRIES = parseInt(getEnvVar('VITE_MAX_RETRIES', '3'));
const RETRY_DELAY = parseInt(getEnvVar('VITE_RETRY_DELAY', '1000'));

console.log('SurveyService: Using API URL:', API_BASE_URL);

// Create axios instance with defaults
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'X-Client-Version': getEnvVar('VITE_APP_VERSION', '1.0.0'),
  },
  withCredentials: true,
});

// Request queue for offline support
let requestQueue = [];
let isOnline = navigator.onLine;

window.addEventListener('online', () => {
  isOnline = true;
  processQueuedRequests();
});

window.addEventListener('offline', () => {
  isOnline = false;
});

const processQueuedRequests = async () => {
  while (requestQueue.length > 0) {
    const request = requestQueue.shift();
    try {
      await request();
    } catch (error) {
      console.error('Error processing queued request:', error);
    }
  }
};

const retryRequest = async (fn, retries = MAX_RETRIES, delay = RETRY_DELAY) => {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 0) throw error;
    
    if (error.response?.status >= 400 && 
        error.response?.status < 500 && 
        error.response?.status !== 408 && 
        error.response?.status !== 429) {
      throw error;
    }
    
    console.log(`Retrying request... (${MAX_RETRIES - retries + 1}/${MAX_RETRIES})`);
    await new Promise(resolve => setTimeout(resolve, delay));
    return retryRequest(fn, retries - 1, delay * 2);
  }
};

// Client-aware cache implementation with aggressive invalidation
class SimpleCache {
  constructor(ttl = 5 * 60 * 1000) {
    this.cache = new Map();
    this.ttl = ttl;
  }
  
  makeKey(base, clientId, userId, surveyType) {
    const parts = [base];
    if (surveyType) parts.push(surveyType);
    if (clientId) parts.push(clientId);
    if (userId) parts.push(userId);
    return parts.join(':');
  }
  
  set(key, value, customTtl = null) {
    this.cache.set(key, {
      value,
      timestamp: Date.now(),
      ttl: customTtl || this.ttl
    });
  }
  
  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    
    if (Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return item.value;
  }
  
  clear() {
    this.cache.clear();
  }
  
  delete(key) {
    this.cache.delete(key);
  }
  
  // Clear all cache entries for a specific client
  clearForClient(clientId) {
    const keysToDelete = [];
    for (const key of this.cache.keys()) {
      if (key.includes(`:${clientId}`) || key.includes(`-${clientId}`)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.cache.delete(key));
  }
  
  // Clear all progress-related caches for a client
  clearProgressForClient(clientId) {
    const keysToDelete = [];
    for (const key of this.cache.keys()) {
      if ((key.includes('progress') || key.includes('resp') || key.includes('status')) && 
          (key.includes(`:${clientId}`) || key.includes(`-${clientId}`))) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => {
      console.log(`Clearing cache key: ${key}`);
      this.cache.delete(key);
    });
  }
}

const cache = new SimpleCache();

// Request interceptors
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    config.headers['X-Request-ID'] = generateRequestId();
    config.metadata = { startTime: new Date() };
    
    const isDev = getEnvVar('VITE_NODE_ENV', 'development') === 'development';
    if (isDev) {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`, config.data);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptors
api.interceptors.response.use(
  (response) => {
    const isDev = getEnvVar('VITE_NODE_ENV', 'development') === 'development';
    if (isDev && response.config.metadata) {
      const duration = new Date() - response.config.metadata.startTime;
      console.log(`API Response: ${response.config.url} (${duration}ms)`, response.data);
    }
    return response;
  },
  async (error) => {
    const { config, response } = error;
    
    if (!response && !isOnline) {
      if (config && ['GET', 'HEAD', 'OPTIONS'].includes(config.method.toUpperCase())) {
        requestQueue.push(() => api.request(config));
        return Promise.reject(new Error('Request queued for offline processing'));
      }
    }
    
    if (error.code === 'ECONNABORTED') {
      console.error('Request timeout:', config?.url);
      return Promise.reject(new Error('Request timeout. Please try again.'));
    }
    
    if (response?.status === 401) {
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('authToken');
      
      if (window.location.pathname !== '/login') {
        window.location.href = '/login?expired=true';
      }
      
      return Promise.reject(new Error('Authentication required'));
    }
    
    if (response?.status === 429) {
      const retryAfter = response.headers['retry-after'];
      const delay = retryAfter ? parseInt(retryAfter) * 1000 : 5000;
      
      console.log(`Rate limited. Retrying after ${delay}ms`);
      await new Promise(resolve => setTimeout(resolve, delay));
      
      return api.request(config);
    }
    
    if (response?.status >= 500) {
      console.error(`Server error (${response.status}):`, response.data);
    }
    
    return Promise.reject(error);
  }
);

const generateRequestId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const buildQueryString = (params) => {
  const query = new URLSearchParams();
  Object.keys(params).forEach(key => {
    if (params[key] !== undefined && params[key] !== null) {
      query.append(key, params[key]);
    }
  });
  return query.toString();
};

// =============== RESILIENT HELPERS ===============

export const pickQuestions = (json) => {
  if (!json) return [];
  if (Array.isArray(json)) return json;
  if (Array.isArray(json.questions)) return json.questions;
  if (json.data && Array.isArray(json.data.questions)) return json.data.questions;
  if (Array.isArray(json.items)) return json.items;
  
  if (json.bySection && typeof json.bySection === 'object') {
    const questions = [];
    Object.values(json.bySection).forEach(section => {
      if (Array.isArray(section.questions)) {
        questions.push(...section.questions);
      }
    });
    return questions;
  }
  
  return [];
};

export const pickResponses = (json) => {
  if (!json) return [];
  if (Array.isArray(json)) return json;
  if (Array.isArray(json.responses)) return json.responses;
  if (json.data && Array.isArray(json.data.responses)) return json.data.responses;
  if (Array.isArray(json.items)) return json.items;
  
  if (json.responses && typeof json.responses === 'object' && !Array.isArray(json.responses)) {
    return Object.entries(json.responses).map(([question_id, response]) => ({
      question_id,
      response: typeof response === 'object' && response && 'response' in response 
        ? response.response 
        : response
    }));
  }
  
  if (typeof json === 'object') {
    const entries = Object.entries(json);
    const skipKeys = ['client_id', 'user_id', 'survey_type', 'timestamp', 'metadata', 'created_at', 'updated_at', 'status', 'progress', 'completed', 'version', 'client_timestamp', 'merged_at'];
    const filtered = entries.filter(([key]) => !skipKeys.includes(key));
    if (filtered.length) {
      return filtered.map(([question_id, response]) => ({
        question_id,
        response: typeof response === 'object' && response && 'response' in response 
          ? response.response 
          : response
      }));
    }
  }
  
  return [];
};

export const pickStatus = (json) => {
  if (!json) return { completed: false, progress: 0 };
  if (json.status) return json.status;
  if (json.data && json.data.status) return json.data.status;
  
  if (json.progress !== undefined) {
    return {
      completed: json.progress === 100,
      progress: json.progress
    };
  }
  
  return { completed: false, progress: 0 };
};

// =============== RESILIENT FETCH HELPERS ===============

async function fetchWithFallbacks(endpoints, options = {}) {
  for (const endpoint of endpoints) {
    try {
      const response = await api.get(endpoint, options);
      if (response.data) {
        return response.data;
      }
    } catch (err) {
      if (err.response?.status !== 404 && err.response?.status !== 405) {
        console.error(`Error fetching from ${endpoint}:`, err);
      }
    }
  }
  return null;
}

// =============== CLIENT-AWARE API METHODS ===============

/**
 * Get survey definition (questions and sections)
 */
export const getSurveyDefinition = async (surveyType, options = {}) => {
  const clientId = options.clientId || localStorage.getItem('activeClientId') || '1';
  const cacheKey = cache.makeKey('survey-definition', clientId, null, surveyType);
  
  if (!options.skipCache) {
    const cached = cache.get(cacheKey);
    if (cached) {
      console.log('Using cached survey definition');
      return cached;
    }
  }
  
  try {
    const qs = `?client_id=${encodeURIComponent(clientId)}`;
    const endpoints = [
      `/api/survey/questions/${surveyType}`,  // This works! From loadSurveyQuestions route
      `/api/survey/${surveyType}/questions${qs}`,
      `/survey/${surveyType}/questions${qs}`,
      `/api/surveys/${surveyType}/questions${qs}`,
      `/surveys/${surveyType}/questions${qs}`,
    ];
    
    const data = await fetchWithFallbacks(endpoints);
    if (data) {
      if (!data.sections) data.sections = [];
      if (!data.questions) data.questions = pickQuestions(data);
      
      cache.set(cacheKey, data);
      return data;
    }
    
    console.warn('Using mock survey data');
    return getMockSurveyData(surveyType);
  } catch (error) {
    console.error('Error fetching survey definition:', error);
    throw error;
  }
};

export const loadSurveyQuestions = async (surveyType, clientId) => {
  return getSurveyDefinition(surveyType, { clientId });
};

/**
 * Get saved survey responses
 */
export const getSurveyResponses = async (clientId, surveyType, options = {}) => {
  const userId = options.userId || 'admin';
  const cacheKey = cache.makeKey('resp', surveyType, clientId, userId);
  
  if (!options.skipCache) {
    const cached = cache.get(cacheKey);
    if (cached) {
      console.log(`Using cached responses for ${cacheKey}`);
      return cached;
    }
  }
  
  try {
    const qs = `?client_id=${encodeURIComponent(clientId)}&user_id=${encodeURIComponent(userId)}`;
    const endpoints = [
      `/api/survey/${surveyType}/responses${qs}`,
      `/api/survey/responses/${surveyType}${qs}`,
      `/api/${surveyType}/responses${qs}`,
      `/survey/${surveyType}/responses${qs}`,
      `/api/surveys/${surveyType}/responses${qs}`,
      `/surveys/${surveyType}/responses${qs}`,
    ];
    
    const data = await fetchWithFallbacks(endpoints);
    if (data) {
      cache.set(cacheKey, data, 60000); // 1 minute TTL for responses
      return data;
    }
    
    // If no endpoint works, return empty responses so survey can still load
    console.warn(`No response endpoints working for ${surveyType}, returning empty responses`);
    return { responses: [], message: 'No saved responses found' };
  } catch (error) {
    console.error('Error fetching survey responses:', error);
    if (error.response?.status === 404) {
      return { responses: [], message: 'No saved responses found' };
    }
    throw error;
  }
};

export const loadResponses = async (surveyType, { client_id, user_id }) => {
  return getSurveyResponses(client_id, surveyType, { userId: user_id });
};

/**
 * Whether a survey answer counts toward progress.
 * Empty strings, null, and empty multi-select arrays are not answered.
 */
export const isSurveyAnswerAnswered = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.some((item) => isSurveyAnswerAnswered(item));
  if (typeof value === 'number') return !Number.isNaN(value);
  if (typeof value === 'boolean') return true;
  if (typeof value === 'object') {
    const nested = value.response ?? value.answer ?? value.value;
    if (nested !== undefined) return isSurveyAnswerAnswered(nested);
    return Object.keys(value).length > 0;
  }
  return Boolean(value);
};

export const countAnsweredSurveyResponses = (responses = {}, questionIds = null) => {
  if (questionIds) {
    return questionIds.filter((id) => isSurveyAnswerAnswered(responses[id])).length;
  }
  return Object.values(responses).filter((value) => isSurveyAnswerAnswered(value)).length;
};

export const deriveSurveyProgressStatus = (answered, total) => {
  if (!total || answered <= 0) return 'not_started';
  if (answered >= total) return 'complete';
  return 'in_progress';
};

/**
 * Save survey answer - CRITICAL: Must bust all caches
 */
export const saveSurveyAnswer = async (surveyType, payload) => {
  // CRITICAL: Clear all related caches immediately
  const { client_id, user_id } = payload;
  
  // Clear response cache with proper key format
  const respCacheKey = cache.makeKey('resp', surveyType, client_id, user_id);
  cache.delete(respCacheKey);
  
  // Clear progress cache
  const progressKey = cache.makeKey('all-survey-progress', client_id);
  cache.delete(progressKey);
  
  // Clear any other progress-related caches for this client
  cache.clearProgressForClient(client_id);
  
  console.log(`Cleared caches for client ${client_id} after save`);
  
  const endpoints = [
    `/api/survey/${surveyType}/answer`,
    `/api/survey/answer/${surveyType}`,
    `/api/${surveyType}/answer`,
    `/api/surveys/${surveyType}/answer`,
    `/survey/${surveyType}/answer`,
    `/surveys/${surveyType}/answer`,
  ];
  
  // For now, just save to localStorage as a fallback
  // since the backend endpoints aren't working
  const localKey = `survey-${surveyType}-${client_id}-${user_id}`;
  let localData = {};
  try {
    localData = JSON.parse(localStorage.getItem(localKey) || '{}');
  } catch (e) {
    localData = {};
  }
  
  if (!isSurveyAnswerAnswered(payload.response)) {
    delete localData[payload.question_id];
  } else {
    localData[payload.question_id] = {
      response: payload.response,
      updated_at: new Date().toISOString(),
      section: payload.section,
      question_type: payload.question_type,
    };
  }
  
  localStorage.setItem(localKey, JSON.stringify(localData));
  
  for (const endpoint of endpoints) {
    try {
      const response = await api.post(endpoint, payload);
      if (response.data) {
        return response.data;
      }
    } catch (err) {
      if (err.response?.status === 404 || err.response?.status === 405) {
        continue;
      }
      // Don't throw error, just log it and use localStorage fallback
      console.log(`Save endpoint ${endpoint} failed, using localStorage fallback`);
    }
  }
  
  return { success: true, saved_locally: true };
};

/**
 * Save survey responses
 */
export const saveSurveyResponses = async (clientId, surveyType, data, options = {}) => {
  try {
    const userId = data.user_id || options.userId || 'admin';
    
    // Clear caches
    cache.clearProgressForClient(clientId);
    
    const payload = {
      client_id: clientId,
      survey_type: surveyType,
      user_id: userId,
      ...data,
      version: data.version || 1,
      client_timestamp: new Date().toISOString(),
    };
    
    try {
      const response = await retryRequest(() => 
        api.post('/survey/responses', payload, {
          headers: {
            'X-Idempotency-Key': options.idempotencyKey || generateRequestId(),
          }
        })
      );
      
      return response.data;
    } catch (error) {
      if (error.response?.status === 409) {
        console.warn('Conflict detected, attempting to merge responses');
        const serverData = await getSurveyResponses(clientId, surveyType, { skipCache: true, userId });
        const mergedData = mergeResponses(serverData, data);
        return saveSurveyResponses(clientId, surveyType, mergedData, options);
      }
      throw error;
    }
  } catch (error) {
    console.error('Error saving survey responses:', error);
    
    if (!isOnline) {
      queueOfflineSave(clientId, surveyType, data);
      return { queued: true, ...data };
    }
    
    throw error;
  }
};

/**
 * Batch save multiple survey responses
 */
export const batchSaveSurveyResponses = async (responses) => {
  try {
    const response = await retryRequest(() =>
      api.post('/survey/responses/batch', { responses })
    );
    
    responses.forEach(r => {
      cache.clearProgressForClient(r.client_id);
    });
    
    return response.data;
  } catch (error) {
    console.error('Error batch saving survey responses:', error);
    throw error;
  }
};

/**
 * Complete a survey
 */
export const completeSurvey = async (surveyType, payload) => {
  const { client_id, user_id } = payload;
  
  try {
    // Clear ALL caches for this client to ensure fresh data everywhere
    cache.clearForClient(client_id);
    
    const endpoints = [
      `/api/survey/${surveyType}/complete`,
      `/api/surveys/${surveyType}/complete`,
      `/survey/${surveyType}/complete`,
      `/surveys/${surveyType}/complete`,
    ];
    
    for (const endpoint of endpoints) {
      try {
        const response = await api.post(endpoint, {
          ...payload,
          completed_at: new Date().toISOString(),
        }, {
          timeout: 180000,
        });
        if (response.data) {
          return response.data;
        }
      } catch (err) {
        if (err.response?.status === 404 || err.response?.status === 405) {
          continue;
        }
        throw err;
      }
    }
    
    return {};
  } catch (error) {
    console.error('Error completing survey:', error);
    throw error;
  }
};

/**
 * Get survey status
 */
export const getSurveyStatus = async (clientId, surveyType) => {
  const cacheKey = cache.makeKey('survey-status', clientId, null, surveyType);
  const cached = cache.get(cacheKey);
  if (cached) return cached;
  
  try {
    const response = await api.get(`/survey/status/${clientId}/${surveyType}?client_id=${clientId}`);
    cache.set(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching survey status:', error);
    
    if (error.response?.status === 404) {
      return {
        completed: false,
        started: false,
        progress: 0,
      };
    }
    throw error;
  }
};

/**
 * Get all surveys status for a client
 */
export const getAllSurveysStatus = async (clientId) => {
  const cacheKey = cache.makeKey('all-surveys-status', clientId);
  const cached = cache.get(cacheKey);
  if (cached) return cached;
  
  try {
    const response = await api.get(`/survey/status/${clientId}?client_id=${clientId}`);
    cache.set(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching all surveys status:', error);
    throw error;
  }
};

/**
 * Get all survey progress - UPDATED TO USE BACKEND ENDPOINT
 */
export const getAllProgress = async (clientId, forceRefresh = false) => {
  const cacheKey = cache.makeKey('all-survey-progress', clientId);
  
  // Use cache only if not forcing refresh
  if (!forceRefresh) {
    const cached = cache.get(cacheKey);
    if (cached) {
      console.log('Using cached survey progress:', cached);
      return cached;
    }
  }
  
  console.log(`Fetching progress from backend for client ${clientId}`);
  
  try {
    // Call the backend progress endpoint
    const response = await api.get(`/survey/progress/${clientId}/all`);
    
    // Handle the response format
    let progressData;
    if (response.data && response.data.success && response.data.data) {
      // Standard format: { success: true, data: {...} }
      progressData = response.data.data;
    } else if (response.data && (response.data.strategy || response.data.capabilities || response.data.readiness)) {
      // Data at root of response.data
      progressData = {
        strategy: response.data.strategy || { done: 0, total: 9, status: 'not_started' },
        capabilities: response.data.capabilities || { done: 0, total: 14, status: 'not_started' },
        readiness: response.data.readiness || { done: 0, total: 14, status: 'not_started' }
      };
    } else {
      // Fallback to empty progress
      console.warn('Unexpected progress response format:', response.data);
      progressData = {
        strategy: { done: 0, total: 9, status: 'not_started' },
        capabilities: { done: 0, total: 14, status: 'not_started' },
        readiness: { done: 0, total: 14, status: 'not_started' }
      };
    }
    
    // Ensure each survey has all required fields
    ['strategy', 'capabilities', 'readiness'].forEach(surveyType => {
      if (!progressData[surveyType]) {
        progressData[surveyType] = { 
          done: 0, 
          answered: 0,
          total: surveyType === 'strategy' ? 9 : 14, 
          status: 'not_started' 
        };
      }
      // Ensure 'answered' field exists for compatibility
      if (progressData[surveyType].done !== undefined && progressData[surveyType].answered === undefined) {
        progressData[surveyType].answered = progressData[surveyType].done;
      }
      // Ensure 'done' field exists if only 'answered' is present
      if (progressData[surveyType].answered !== undefined && progressData[surveyType].done === undefined) {
        progressData[surveyType].done = progressData[surveyType].answered;
      }

      const answered = progressData[surveyType].answered ?? progressData[surveyType].done ?? 0;
      const total = progressData[surveyType].total || (surveyType === 'strategy' ? 9 : 14);
      progressData[surveyType].answered = answered;
      progressData[surveyType].done = answered;
      progressData[surveyType].total = total;
      progressData[surveyType].status = deriveSurveyProgressStatus(answered, total);
      progressData[surveyType].percentage = total > 0 ? Math.round((answered / total) * 100) : 0;
    });
    
    console.log('Progress data from backend:', progressData);
    
    // Cache with 30 second TTL
    cache.set(cacheKey, progressData, 30000);
    
    return progressData;
    
  } catch (error) {
    console.error('Error fetching progress from backend:', error);
    
    // Return default structure on error
    const defaultProgress = {
      strategy: { done: 0, answered: 0, total: 9, status: 'not_started' },
      capabilities: { done: 0, answered: 0, total: 14, status: 'not_started' },
      readiness: { done: 0, answered: 0, total: 14, status: 'not_started' }
    };
    
    // Still cache even the default to prevent repeated failing calls
    cache.set(cacheKey, defaultProgress, 10000); // Shorter TTL for error case
    
    return defaultProgress;
  }
};

/**
 * Clear progress cache for a specific client
 */
export const clearProgressCache = (clientId) => {
  cache.clearProgressForClient(clientId);
  console.log(`Cleared all progress caches for client ${clientId}`);
};

/**
 * Upload file for survey question
 */
export const uploadSurveyFile = async (file, metadata = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  Object.keys(metadata).forEach(key => {
    formData.append(key, metadata[key]);
  });
  
  try {
    const response = await retryRequest(() =>
      api.post('/survey/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          if (metadata.onProgress) {
            metadata.onProgress(percentCompleted);
          }
        },
      })
    );
    
    return response.data;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

/**
 * Download survey responses as file
 */
export const downloadSurveyResponses = async (clientId, surveyType, format = 'json') => {
  try {
    const response = await api.get(`/survey/export/${clientId}/${surveyType}`, {
      params: { format, client_id: clientId },
      responseType: 'blob',
    });
    
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `survey-${surveyType}-${clientId}-${Date.now()}.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Error downloading survey responses:', error);
    throw error;
  }
};

/**
 * Validate survey responses
 */
export const validateSurveyResponses = async (surveyType, responses) => {
  try {
    const response = await api.post(`/survey/${surveyType}/validate`, { responses });
    return response.data;
  } catch (error) {
    console.error('Error validating survey responses:', error);
    
    if (error.response?.status === 422) {
      return error.response.data;
    }
    throw error;
  }
};

/**
 * Get survey analytics
 */
export const getSurveyAnalytics = async (clientId, surveyType) => {
  const cacheKey = cache.makeKey('survey-analytics', clientId, null, surveyType);
  const cached = cache.get(cacheKey);
  if (cached) return cached;
  
  try {
    const response = await api.get(`/survey/analytics/${clientId}/${surveyType}?client_id=${clientId}`);
    cache.set(cacheKey, response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching survey analytics:', error);
    throw error;
  }
};

/**
 * Get survey summary
 */
export const getSummary = async (clientId, summaryType) => {
  try {
    const response = await api.get(`/summary/${clientId}/${summaryType}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching summary:', error);
    throw error;
  }
};

/**
 * Normalize responses from various formats
 */
export const normalizeResponses = (rawData, questions) => {
  if (!rawData) return {};
  
  let normalized = {};
  
  if (rawData.responses && typeof rawData.responses === 'object') {
    normalized = { ...rawData.responses };
  } else if (Array.isArray(rawData)) {
    rawData.forEach(item => {
      const questionId = item.question_id || item.id || item.questionId;
      const value = item.response || item.value || item.answer;
      if (questionId) {
        normalized[questionId] = value;
      }
    });
  } else if (typeof rawData === 'object') {
    Object.keys(rawData).forEach(key => {
      if (!['client_id', 'survey_type', 'timestamp', 'progress', 'completed', 
            'version', 'metadata', 'created_at', 'updated_at', 'user_id', 
            'client_timestamp', 'merged_at'].includes(key)) {
        normalized[key] = rawData[key];
      }
    });
  }
  
  questions.forEach(question => {
    const questionId = question.id;
    const value = normalized[questionId];
    
    if (value !== undefined && value !== null) {
      const responseType = question.response_type || question.type;
      const isMultiSelect = ['multi_select', 'checkbox'].includes(responseType);
      
      if (isMultiSelect) {
        if (typeof value === 'string') {
          if (value.includes(',')) {
            normalized[questionId] = value.split(',').map(v => v.trim()).filter(v => v);
          } else if (value.includes(';')) {
            normalized[questionId] = value.split(';').map(v => v.trim()).filter(v => v);
          } else {
            normalized[questionId] = value ? [value] : [];
          }
        } else if (!Array.isArray(value)) {
          normalized[questionId] = value ? [value] : [];
        }
      } else if (responseType === 'number') {
        normalized[questionId] = value === '' ? null : Number(value);
      } else if (responseType === 'boolean') {
        normalized[questionId] = value === 'true' || value === true;
      } else {
        if (Array.isArray(value) && value.length > 0) {
          normalized[questionId] = value[0];
        }
      }
    }
  });
  
  return normalized;
};

const mergeResponses = (serverData, clientData) => {
  const merged = { ...serverData };
  
  merged.responses = { ...serverData.responses, ...clientData.responses };
  merged.version = (serverData.version || 0) + 1;
  merged.merged_at = new Date().toISOString();
  
  if (clientData.progress && serverData.progress) {
    merged.progress = {
      answered: clientData.progress.answered ?? serverData.progress.answered ?? 0,
      total: clientData.progress.total || serverData.progress.total,
      percentage: clientData.progress.percentage ?? serverData.progress.percentage ?? 0,
    };
  }
  
  return merged;
};

const queueOfflineSave = (clientId, surveyType, data) => {
  const key = `offline-survey-${clientId}-${surveyType}`;
  const queue = JSON.parse(localStorage.getItem('offline-survey-queue') || '[]');
  
  const existingIndex = queue.findIndex(item => 
    item.clientId === clientId && item.surveyType === surveyType
  );
  
  const queueItem = {
    clientId,
    surveyType,
    data,
    timestamp: new Date().toISOString(),
  };
  
  if (existingIndex >= 0) {
    queue[existingIndex] = queueItem;
  } else {
    queue.push(queueItem);
  }
  
  localStorage.setItem('offline-survey-queue', JSON.stringify(queue));
  localStorage.setItem(key, JSON.stringify(data));
};

export const processOfflineQueue = async () => {
  const queue = JSON.parse(localStorage.getItem('offline-survey-queue') || '[]');
  const processed = [];
  const failed = [];
  
  for (const item of queue) {
    try {
      await saveSurveyResponses(item.clientId, item.surveyType, item.data);
      processed.push(item);
      
      localStorage.removeItem(`offline-survey-${item.clientId}-${item.surveyType}`);
    } catch (error) {
      console.error('Failed to process offline item:', error);
      failed.push(item);
    }
  }
  
  localStorage.setItem('offline-survey-queue', JSON.stringify(failed));
  
  return {
    processed: processed.length,
    failed: failed.length,
    remaining: failed,
  };
};

export const clearAllCaches = () => {
  cache.clear();
  console.log('All caches cleared');
};

const getMockSurveyData = (surveyType) => {
  const mockData = {
    strategy: {
      sections: [
        { id: 'goals', title: 'Strategic Goals', description: 'Define your primary objectives', order: 1 },
        { id: 'priorities', title: 'Priorities', description: 'Identify key focus areas', order: 2 },
        { id: 'challenges', title: 'Challenges', description: 'Acknowledge potential obstacles', order: 3 },
      ],
      questions: Array.from({ length: 9 }, (_, i) => ({
        id: `q${i + 1}`,
        section_id: i < 3 ? 'goals' : i < 6 ? 'priorities' : 'challenges',
        question_text: `Question ${i + 1}`,
        response_type: i % 3 === 0 ? 'long_text' : i % 3 === 1 ? 'single_select' : 'multi_select',
        options: i % 3 !== 0 ? ['Option A', 'Option B', 'Option C'] : undefined,
        required: true,
        order: i + 1,
      })),
    },
    capabilities: {
      sections: [
        { id: 'technical', title: 'Technical Capabilities', order: 1 },
        { id: 'organizational', title: 'Organizational Capabilities', order: 2 },
      ],
      questions: Array.from({ length: 14 }, (_, i) => ({
        id: `c${i + 1}`,
        section_id: i < 7 ? 'technical' : 'organizational',
        question_text: `Capability Question ${i + 1}`,
        response_type: i % 2 === 0 ? 'single_select' : 'rating',
        options: i % 2 === 0 ? ['Basic', 'Intermediate', 'Advanced', 'Expert'] : undefined,
        max_rating: i % 2 === 1 ? 5 : undefined,
        required: true,
        order: i + 1,
      })),
    },
    readiness: {
      sections: [
        { id: 'assessment', title: 'Readiness Assessment', order: 1 },
      ],
      questions: Array.from({ length: 14 }, (_, i) => ({
        id: `r${i + 1}`,
        section_id: 'assessment',
        question_text: `Readiness Question ${i + 1}`,
        response_type: i % 3 === 0 ? 'single_select' : i % 3 === 1 ? 'long_text' : 'rating',
        options: i % 3 === 0 ? ['Not Ready', 'Somewhat Ready', 'Ready', 'Very Ready'] : undefined,
        max_rating: i % 3 === 2 ? 5 : undefined,
        required: true,
        order: i + 1,
      })),
    },
  };
  
  return mockData[surveyType] || mockData.strategy;
};

// Process offline queue on load if online
if (isOnline) {
  processOfflineQueue().then(result => {
    if (result.processed > 0) {
      console.log(`Processed ${result.processed} offline survey saves`);
    }
  });
}

// Export as default for backward compatibility
export default {
  getSurveyDefinition,
  loadSurveyQuestions,
  getSurveyResponses,
  loadResponses,
  saveSurveyResponses,
  saveSurveyAnswer,
  batchSaveSurveyResponses,
  completeSurvey,
  normalizeResponses,
  getSurveyStatus,
  getAllSurveysStatus,
  getAllProgress,
  clearProgressCache,
  uploadSurveyFile,
  downloadSurveyResponses,
  validateSurveyResponses,
  getSurveyAnalytics,
  getSummary,
  processOfflineQueue,
  clearAllCaches,
  pickQuestions,
  pickResponses,
  pickStatus,
  isSurveyAnswerAnswered,
  countAnsweredSurveyResponses,
  deriveSurveyProgressStatus,
};